import PrintFormatter from "@/lib/printer/printFormatter";
import Axios from "axios";
import { Format } from "@/util/Format";

const DONE = "DONE";
const RECEIVED_RESPONSE = "RECEIVED_RESPONSE";

class AmpUSA {

  constructor() {
    this._api = "https://cloud.amp360.amobilepayment.com/api";
    this._sourceSerial = "AMP360";
    this._transactionEndpoint = "TRANSACTION";
    this._systemEndpoint = "SYSTEM";
    this._displayEndpoint = "DISPLAY";
    this._commandQueueUrl = `${this._api}/command/queue`;
    this._terminalSerialNumber = null;
    this._authenticationCode = null;
    this._waitingForTransaction = false;
    this._currentPurchaseUUID = null;
  }

  /**
   * @param {string} terminalSerialNumber
   * @param {string} authenticationCode
   */
  init(terminalSerialNumber, authenticationCode) {
    return new Promise((resolve) => {
      this._terminalSerialNumber = terminalSerialNumber;
      this._authenticationCode = authenticationCode;
      resolve();
    });
  }

  /**
   * @returns {Promise}
   */
  logoutTerminal() {
    let interval;
    const intervalDelay = 3000; //ms

    return new Promise((resolve, reject) => {
      this._logout()
        .then((commandId) => {
          interval = setInterval(() => {
            this.getTransactionStatus(commandId)
              .then((response) => {
                if (response.status === DONE || response.status === RECEIVED_RESPONSE) {
                  clearInterval(interval);
                  resolve(this._parseResponse(response.responseData));
                }
              })
              .catch(function (error) {
                clearInterval(interval);
                reject(error);
              });
          }, intervalDelay);
        });
    });
  }

  /**
   * @returns {Promise}
   * @private
   */
  _logout() {
    return new Promise((resolve, reject) => {
      Axios.post(this._commandQueueUrl, {
        sourceSerial: this._sourceSerial,
        targetTerminal: this._terminalSerialNumber,
        authCode: this._authenticationCode,
        payload: {
          Endpoint: this._transactionEndpoint,
          cmdType: "TerminalLogout",
          ReqPayload: { }
        }
      }).then((response) => {
        resolve(response.data.result.commandId);
      }).catch((error) => {
        reject({ type: "server", message: error.data, error: error });
      });
    });
  }

  /**
   * @returns {Promise}
   */
  initializeTerminal() {
    let interval;
    const intervalDelay = 3000; //ms

    return new Promise((resolve, reject) => {
      this._initialize()
        .then((commandId) => {
          interval = setInterval(() => {
            this.getTransactionStatus(commandId)
              .then((response) => {
                if (response.status === DONE || response.status === RECEIVED_RESPONSE) {
                  clearInterval(interval);
                  resolve(this._parseResponse(response.responseData));
                }
              })
              .catch(function (error) {
                clearInterval(interval);
                reject(error);
              });
          }, intervalDelay);
      });
    });
  }

  /**
   * @returns {Promise}
   * @private
   */
  _initialize() {
    return new Promise((resolve, reject) => {
      Axios.post(this._commandQueueUrl, {
        sourceSerial: this._sourceSerial,
        targetTerminal: this._terminalSerialNumber,
        authCode: this._authenticationCode,
        payload: {
          Endpoint: this._transactionEndpoint,
          cmdType: "TerminalInitialization",
          ReqPayload: { }
        }
      }).then((response) => {
        resolve(response.data.result.commandId);
      }).catch((error) => {
        reject({ type: "server", message: error.data, error: error });
      });
    });
  }

  /**
   * @returns {Promise}
   */
  initializeDisplay() {
    const intervalDelay = 3000; //ms
    const maximumWait = 15 * 1000; // 15 seconds
    const maximumIterations = maximumWait / intervalDelay; //How many times the loop will go through
    let iterations = 0;
    let interval;
    this._waitingForTransaction = true;

    return new Promise((resolve, reject) => {
      this._initializeDisplay()
        .then((commandId) => {
          interval = setInterval(() => {
            if (iterations >= maximumIterations) {
              clearInterval(interval);
              this._waitingForTransaction = false;
              reject({ error: "AMP-error", response: "unreachable" });
            }
            iterations++;
            this.getTransactionStatus(commandId)
              .then((response) => {
                if (response.status === RECEIVED_RESPONSE) {
                  clearInterval(interval);
                  this._waitingForTransaction = false;
                  resolve(true);
                }
              }).catch((error) => {
                clearInterval(interval);
                this._waitingForTransaction = false;
                reject(error);
              });
          }, intervalDelay);
        });
    });
  }

  /**
   * @returns {Promise}
   * @private
   */
  _initializeDisplay() {
    return new Promise((resolve, reject) => {
      Axios.post(this._commandQueueUrl, {
        sourceSerial: this._sourceSerial,
        targetTerminal: this._terminalSerialNumber,
        authCode: this._authenticationCode,
        payload: {
          Endpoint: this._displayEndpoint,
          cmdType: "DisplayText",
          ReqPayload: {
            DisplayData: "<div style='text-align:center;padding-top: 200px;width:100%;height:100%;'>" +
              "<img style='width: 50%; height: auto;' src='data:image/png;base64,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' style='width: 70%; height: auto;' />" +
              "</div>"
          }
        }
      }).then((response) => {
        if (response.data.canReload) {
          resolve(response.data.result.commandId);
        } else {
          reject({ type: "server", message: response.data });
        }
      }).catch((error) => {
        reject({ type: "server", message: error.data });
      });
    });
  }

  /**
   * @returns {Promise}
   */
  getChargeTransactionResult() {
    const intervalDelay = 3000; //ms
    const maximumWait = 30 * 1000; //seconds
    const maximumIterations = maximumWait / intervalDelay; //How many times the loop will go through
    let iterations = 0;
    let interval;

    return new Promise((resolve, reject) => {
      this._log("AMP - Started getChargeTransactionResult");
      this._getTransactionResult()
        .then((commandId) => {
          interval = setInterval(() => {
            if (iterations >= maximumIterations) {
              clearInterval(interval);
              reject({ error: "AMP-error", response: "unreachable" });
            }
            iterations++;
            this.getTransactionStatus(commandId)
              .then((response) => {
                if (response.status === DONE || response.status === RECEIVED_RESPONSE) {
                  clearInterval(interval);
                  let data = this._parseResponse(response.responseData);
                  if (data.Payload.response_resultcode_key === "0000" && data.Payload.response_code_key === "00") {
                    resolve(data);
                  } else {
                    reject({ error: "AMP-error", response: data });
                  }
                  resolve(data);
                }
              }).catch((error) => {
                clearInterval(interval);
                reject(error);
              });
          }, intervalDelay);
        });
    });
  }

  /**
   * @returns {Promise}
   * @private
   */
  _getTransactionResult() {
    return new Promise((resolve, reject) => {
      Axios.post(this._commandQueueUrl, {
        sourceSerial: this._sourceSerial,
        targetTerminal: this._terminalSerialNumber,
        authCode: this._authenticationCode,
        payload: {
          Endpoint: this._transactionEndpoint,
          cmdType: "GetTransactionResult",
          ReqPayload: { }
        }
      }).then((response) => {
        if (response.data.canReload) {
          resolve(response.data.result.commandId);
        } else {
          reject({ type: "server", message: response.data });
        }
      }).catch((error) => {
        reject({ type: "server", message: error.data });
      });
    });
  }

  /**
   * @param {string|number} commandId
   * @return {Promise}
   */
  getTransactionStatus(commandId) {
    this._log("AMP - Getting Transaction status for command " + commandId);
    return new Promise((resolve, reject) => {
      Axios.get(`${this._api}/command/status/${commandId}/${this._terminalSerialNumber}/${this._authenticationCode}`)
        .then((response) => {
          if (response.data.canReload) {
            resolve(response.data.result);
          } else {
            reject({ type: "server", message: response.data });
          }
        }).catch((error) => {
          if (error && error.response && (error.response.status === 502 || error.response.status === 504)) {
            this._log("Caught " + error.response.status + " error.");
            resolve({});
          }
          reject({ type: "server", message: error.data, error: error });
      });
    });
  }

  /**
   * @param {number} amount
   * @returns {Promise}
   */
  charge(amount) {
    const intervalDelay = 3000; //ms
    const maximumWait = 45 * 1000; //seconds
    const maximumIterations = maximumWait / intervalDelay; //How many times the loop will go through
    let iterations = 0;
    let interval;
    let started = false;
    this._currentPurchaseUUID = this._generateUUID();

    return new Promise((resolve, reject) => {
      let waitInterval = setInterval(() => {
        if (started || interval) {
          this._log("AMP - Cancel wait interval");
          clearInterval(waitInterval);
          return;
        }
        if (!this._waitingForTransaction) {
          started = true;
          clearInterval(waitInterval);

          this._purchase(amount)
            .then((commandId) => {
              this._log("AMP - _purchase then");
              interval = setInterval(() => {
                if (iterations >= maximumIterations) {
                  clearInterval(interval);
                  reject({ error: "AMP-error", response: "timeout" });
                }
                iterations++;
                this.getTransactionStatus(commandId)
                  .then((response) => {
                    if (response.status === DONE || response.status === RECEIVED_RESPONSE) {
                      clearInterval(interval);
                      let data = this._parseResponse(response.responseData);
                      if (data.Payload && data.Payload.response_resultcode_key === "0000" && data.Payload.response_code_key === "00") {
                        resolve(data);
                      } else {
                        reject({ error: "AMP-error", response: data });
                      }
                      resolve(data);
                    }
                  }).catch((error) => {
                    clearInterval(interval);
                    console.log("error", error);
                    reject(error);
                  });
              }, intervalDelay);
            })
            .catch((error) => {
              this._log("AMP - _purchase error");
              this._log(error);
              reject(error);
            });
        } else {
          console.log("transaction waiting");
        }
      }, 250);

    });
  }

  /**
   * @param amount
   * @returns {Promise}
   * @private
   */
  _purchase(amount) {
    this._log("AMP - Started _purchase");
    return new Promise((resolve, reject) => {
      Axios.post(this._commandQueueUrl, {
        sourceSerial: this._sourceSerial,
        targetTerminal: this._terminalSerialNumber,
        authCode: this._authenticationCode,
        payload: {
          Endpoint: this._transactionEndpoint,
          cmdType: "Purchase",
          ReqPayload: {
            BaseAmount: amount.toFixed(2),
            CardEntryMethod: "AUTO",
            AutoPrint: "false",
            UserDefinedEchoData: this._currentPurchaseUUID
          }
        }
      }).then((response) => {
        if (response.data.canReload) {
          resolve(response.data.result.commandId);
        } else {
          reject({ type: "server", message: response.data });
        }
      }).catch((error) => {
        reject({ type: "server", message: error.data, error: error });
      });
    });
  }

  /**
   * @returns {Promise}
   */
  reboot() {
    return new Promise((resolve, reject) => {
      Axios.post(this._commandQueueUrl, {
        sourceSerial: this._sourceSerial,
        targetTerminal: this._terminalSerialNumber,
        authCode: this._authenticationCode,
        payload: {
          Endpoint: this._systemEndpoint,
          cmdType: "REBOOT_TERMINAL",
          ReqPayload: { }
        }
      }).then((response) => {
        if (response.data.canReload) {
          resolve(response.data.result.commandId);
        } else {
          reject({ type: "server", message: response.data });
        }
      }).catch((error) => {
        reject({ type: "server", message: error.data, error: error });
      });
    });
  }

  /**
   * @returns {Promise}
   */
  closeBatch() {
    const intervalDelay = 3000; //ms
    let interval;

    return new Promise((resolve, reject) => {
      this._settlement()
        .then((commandId) => {
          interval = setInterval(() => {
            this.getTransactionStatus(commandId)
              .then((response) => {
                if (response.status === DONE || response.status === RECEIVED_RESPONSE) {
                  clearInterval(interval);
                  resolve(this._parseResponse(response.responseData));
                }
              }).catch((error) => {
                clearInterval(interval);
                reject(error);
              });
          }, intervalDelay);
      });
    });
  }

  /**
   * @returns {Promise}
   * @private
   */
  _settlement() {
    return new Promise((resolve, reject) => {
      Axios.post(this._commandQueueUrl, {
        sourceSerial: this._sourceSerial,
        targetTerminal: this._terminalSerialNumber,
        authCode: this._authenticationCode,
        payload: {
          Endpoint: this._transactionEndpoint,
          cmdType: "Settlement",
          ReqPayload: {
            AutoPrint: "false"
          }
        }
      }).then((response) => {
        if (response.data.canReload) {
          resolve(response.data.result.commandId);
        } else {
          reject({ type: "server", message: response.data });
        }
      }).catch((error) => {
        reject({ type: "server", message: error.data, error: error });
      });
    });
  }

  /**
   * @returns {Promise}
   */
  flushTransaction() {
    return new Promise((resolve, reject) => {
      Axios.post(`${this._api}/command/flush`, {
        serialNumber: this._terminalSerialNumber,
        authCode: this._authenticationCode
      }).then((response) => {
        if (response.data.canReload) {
          resolve(response.data.result.commandId);
        } else {
          reject({ type: "server", message: response.data });
        }
      }).catch((error) => {
        reject({ type: "server", message: error.data, error: error });
      });
    });
  }

  /**
   * @param data
   */
  printSettlementReceipt(data) {
    let jsonResponse = Format.parseXmlToJson(data.Payload.response_xmldata_key.toString());
    let printer = new PrintFormatter();
    let settlement = jsonResponse.XMLResponse.XMLReceipt;
    const DEFAULT_PRINTER_COLS = 40;
    let receipt = [];

    receipt.push(spaceBetweenPad(settlement.ReportHeader.TransDate, DEFAULT_PRINTER_COLS, " ", settlement.ReportHeader.TransTime));
    receipt.push(spaceAroundPad(settlement.ReportHeader.ReportLabel, DEFAULT_PRINTER_COLS, " "));
    receipt.push(spaceAroundPad(settlement.BatchRespText, DEFAULT_PRINTER_COLS, " "));
    receipt.push(spaceBetweenPad("FROM: " + settlement.ReportHeader.FromDate, DEFAULT_PRINTER_COLS, " ", settlement.ReportHeader.FromTime));
    receipt.push(spaceBetweenPad("TO: " + settlement.ReportHeader.ToDate, DEFAULT_PRINTER_COLS, " ", settlement.ReportHeader.ToTime));
    receipt.push(spaceBetweenPad("Terminal ID:", DEFAULT_PRINTER_COLS, " ", settlement.ReportHeader.TerminalID));
    receipt.push(spaceBetweenPad("Merchant ID:", DEFAULT_PRINTER_COLS, " ", settlement.ReportHeader.MerchantID));
    receipt.push(spaceBetweenPad("Batch No:", DEFAULT_PRINTER_COLS, " ", settlement.ReportHeader.BatchNo));

    receipt.push(spaceAroundPad("TRANSACTIONS", DEFAULT_PRINTER_COLS, "-"));
    for (let transactionKey in settlement.ReportBody) {
      let transaction = settlement.ReportBody[transactionKey];
      receipt.push(spaceBetweenPad(transaction.TransDate, DEFAULT_PRINTER_COLS, " ", transaction.TransTime));
      receipt.push(spaceAroundPad(transaction.TransName, DEFAULT_PRINTER_COLS, " "));
      receipt.push(spaceBetweenPad(transaction.CardLabel, DEFAULT_PRINTER_COLS, " ", transaction.AccountNumber));
      receipt.push(spaceBetweenPad("Auth Number: " + transaction.AuthNumber, DEFAULT_PRINTER_COLS, " ", "Invoice: " + transaction.InvoiceNo));
      receipt.push(spaceBetweenPad("Reference Number:", DEFAULT_PRINTER_COLS, " ", transaction.ReferenceNumber));
      receipt.push(spaceBetweenPad("Total:", DEFAULT_PRINTER_COLS, " ", transaction.TransAmount));
      receipt.push(rightPad("", DEFAULT_PRINTER_COLS, "-"));
    }

    receipt.push(spaceAroundPad("TOTALS", DEFAULT_PRINTER_COLS, " "));
    receipt.push(spaceBetweenPad("Total records:", DEFAULT_PRINTER_COLS, " ", settlement.TerminalTotals.BatchTotalRecords));
    receipt.push(spaceBetweenPad("Total Void count:", DEFAULT_PRINTER_COLS, " ", settlement.TerminalTotals.TotalVoidCount));
    receipt.push(spaceBetweenPad("Net sales:", DEFAULT_PRINTER_COLS, " ", settlement.TerminalTotals.NetSales));

    for (let i = 0; i < receipt.length; i++) {
      printer.addLine(receipt[i]);
    }
    printer.setPadding(0).print();
  }

  /**
   * @param {*} data
   * @returns {string[]}
   */
  formatChargeResponseForReceipt(data) {
    if (!data || !data.Payload) {
      return [];
    }
    let payload = data.Payload;
    const DEFAULT_PRINTER_COLS = 40;
    let receipt = [];

    if (payload.response_resultcode_key === "0000" && payload.response_code_key === "00") {
      for (let key in payload) {
        if (payload[key] == null) {
          payload[key] = "";
        }
      }

      receipt.push(spaceBetweenPad(payload.response_date_key, DEFAULT_PRINTER_COLS, " ", payload.response_time_key));
      receipt.push(spaceBetweenPad("MID: " + payload.response_mid_key, DEFAULT_PRINTER_COLS, " ", "TID: " + payload.response_tid_key));
      receipt.push(spaceAroundPad(payload.response_transname_key, DEFAULT_PRINTER_COLS, " "));
      receipt.push(spaceBetweenPad(payload.response_cardlabel_key, DEFAULT_PRINTER_COLS, " ", payload.response_pan_key));
      receipt.push(spaceBetweenPad(payload.response_entrymode_key, DEFAULT_PRINTER_COLS, " ", payload.response_cvm_key));
      receipt.push(spaceBetweenPad("Sequence:", DEFAULT_PRINTER_COLS, " ", payload.response_sequencenum_key));
      receipt.push(spaceAroundPad(payload.response_currency_key + " " + parseResponseStringToNumber(payload.response_baseamt_key), DEFAULT_PRINTER_COLS, " "));
      if (payload.response_surchargeamt_key != "") {
        receipt.push(spaceBetweenPad("Surchage:", DEFAULT_PRINTER_COLS, " ", parseResponseStringToNumber(payload.response_surchargeamt_key)));
      }
      if (payload.response_cashbkamt_key != "") {
        receipt.push(spaceBetweenPad("Cashback:", DEFAULT_PRINTER_COLS, " ", parseResponseStringToNumber(payload.response_cashbkamt_key)));
      }
      if (payload.response_tipamt_key != "") {
        receipt.push(spaceBetweenPad("Tip:", DEFAULT_PRINTER_COLS, " ", parseResponseStringToNumber(payload.response_tipamt_key)));
      }
      receipt.push(spaceBetweenPad("Total:", DEFAULT_PRINTER_COLS, " ", parseResponseStringToNumber(payload.response_totalamt_key)));
      receipt.push(spaceAroundPad(payload.response_text_key + " " + payload.response_code_key, DEFAULT_PRINTER_COLS, " "));
      receipt.push(spaceBetweenPad("Auth code:", DEFAULT_PRINTER_COLS, " ", payload.response_authcode_key));
      receipt.push(spaceBetweenPad("Host timestamp:", DEFAULT_PRINTER_COLS, " ", payload.response_hosttimestamp_key));
      receipt.push(spaceAroundPad(payload.response_card_holder_name_key.trim(), DEFAULT_PRINTER_COLS, " "));

    } else {
      let readable_error = "";
      switch (payload.response_resultcode_key) {
        case "-1001":
          readable_error = "CANCELLED";
          break;
        case "-1002":
          readable_error = "TIMEOUT";
          break;
        case "-1005":
          readable_error = "RECORD NOT FOUND";
          break;
        case "-1006":
          readable_error = "TRANSACTION INVALID";
          break;
        default:
          switch (payload.response_code_key) {
            case "51":
              readable_error = "INSUFFICIENT FUNDS";
              break;
            case "54":
              readable_error = "EXPIRED CARD";
              break;
            case "55":
              readable_error = "INVALID PIN";
              break;
            default:
              readable_error = "TRANSACTION ERROR";
              break;
          }
          break;
      }
      let day = new Date().toLocaleString().substring(0, 10);
      let time = new Date().toLocaleString().substring(13);
      receipt.push(spaceBetweenPad(day, DEFAULT_PRINTER_COLS, " ", time));
      receipt.push("");
      receipt.push(spaceAroundPad(readable_error + " " + (payload.response_resultcode_key || ""), DEFAULT_PRINTER_COLS, " "));
    }

    return receipt;
  }

  /**
   * @returns {string}
   * @private
   */
  _generateUUID() {
    let chars = [];
    for (let i = 48; i <= 57; i++) {
      chars.push(i);
    }
    for (let i = 65; i <= 90; i++) {
      chars.push(i);
    }
    for (let i = 97; i <= 122; i++) {
      chars.push(i);
    }
    let uuid = "";
    for (let i = 0; i < 32; i++) {
      uuid += String.fromCharCode(chars[Math.floor(Math.random() * chars.length)]);
    }
    return uuid;
  }

  /**
   * @param {string} responseData
   * @returns {any}
   * @private
   */
  _parseResponse(responseData) {
    let escaped = responseData
      .replace(/\\n/g, "")
      .replace(/\\t/g, "")
      .replace(/\\/g, "")
      .replace(/"{/g, "{")
      .replace(/}"/g, "}");
    let data = "" + escaped;
    return JSON.parse(data.toString());
  }

  /**
   * @param {*} data
   * @private
   */
  async _log(data) {
    if (window.logToFile) {
      await window.logToFile(data);
    } else {
      console.log(data);
    }
  }

}

const parseResponseStringToNumber = function(string) {
  if (string == "") {
    return "0.00";
  } else {
    if (isNaN(parseFloat(string))) {
      return "0.00";
    } else {
      return (parseFloat(string) / 100).toFixed(2);
    }
  }
};

let ampUSA = new AmpUSA();
export { ampUSA as AmpUSA };
